import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import { API_BASE_URL } from '../services/index';
import { MonthCalendar, addOneMonth, startDateBounds, todayLocalISO, fmt, fmtDateTime } from './SubscriptionWindowModal';
import './SubscriptionWindowModal.css';

/**
 * MySubscriptionModal — the user's own business subscription period.
 *
 * Offered to every team member of a business allowed by
 * SUBSCRIPTION_SELF_SERVICE_BUSINESS_IDS ('*' = all; backend decides). The user picks a START date; the END is start + 1 month,
 * derived again on the server and guarded by the DB CHECK constraint. After a
 * save the session's subscription status is re-read from /auth/user and the app
 * reloads, so the subscription lock lifts as soon as today is inside the period.
 */

const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('token')}`
});

const istDateOf = (v) => (v ? new Date(v).toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' }) : null);

/** Re-read /auth/user into localStorage so App recomputes the lock on reload. */
async function refreshSessionSubscription() {
  const r = await fetch(`${API_BASE_URL}/auth/user`, { headers: authHeaders() });
  const data = await r.json();
  if (!r.ok || !data?.success) return null;
  const stored = JSON.parse(localStorage.getItem('user') || '{}');
  localStorage.setItem('user', JSON.stringify({
    ...stored,
    subscription: data.subscription,
    isTrialActive: data.isTrialActive,
    trialExpiryDate: data.trialExpiryDate,
    subscriptionSource: data.subscriptionSource,
    subscriptionWindow: data.subscriptionWindow
  }));
  return data;
}

const MySubscriptionModal = ({ onClose }) => {
  const [detail, setDetail] = useState(null);      // { business, history, today }
  const [startDate, setStartDate] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);            // { type: 'ok'|'err', text }

  const serverToday = detail?.today || todayLocalISO();
  const endDate = startDate ? addOneMonth(startDate) : '';
  const { minDate, maxDate } = useMemo(() => startDateBounds(serverToday), [serverToday]);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API_BASE_URL}/subscription-window/self`, { headers: authHeaders() });
      const data = await r.json();
      if (!r.ok || !data.success) throw new Error(data.error || `HTTP ${r.status}`);
      setDetail(data);
      setStartDate((cur) => cur || data.business.window.start || data.today || todayLocalISO());
    } catch (e) {
      setMsg({ type: 'err', text: `Could not load your subscription: ${e.message}` });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const biz = detail?.business;
  const unchanged = biz && biz.window.start === startDate;

  // The period that follows the current one: starts where the current one ends.
  const nextPeriodStart = useMemo(() => {
    if (!biz) return null;
    const curEnd = biz.window.end || istDateOf(biz.accessUntil);
    const candidate = curEnd || serverToday;
    return candidate >= minDate && candidate <= maxDate ? candidate : null;
  }, [biz, serverToday, minDate, maxDate]);

  const warnings = useMemo(() => {
    if (!biz || !startDate) return [];
    const w = [];
    if (startDate > serverToday) w.push(`Starts in the future — the app stays LOCKED until ${fmt(startDate)}.`);
    if (endDate < serverToday) w.push(`This period already ended on ${fmt(endDate)} — the app will be locked.`);
    if (biz.paidSubscription) w.push('This account has the paid-subscription flag, so it stays unlocked regardless of this period.');
    return w;
  }, [biz, startDate, endDate, serverToday]);

  const save = async () => {
    if (!biz || !startDate) return;
    setSaving(true); setMsg(null);
    try {
      const r = await fetch(`${API_BASE_URL}/subscription-window/self`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ startDate, reason })   // no endDate — server derives it
      });
      const data = await r.json();
      if (!r.ok || !data.success) throw new Error(data.error || `HTTP ${r.status}`);
      const session = await refreshSessionSubscription();
      const unlocked = session && (session.subscription === true || session.isTrialActive === true);
      setMsg({
        type: unlocked ? 'ok' : 'err',
        text: unlocked
          ? `${data.message}. Access is active — reloading…`
          : `${data.message}, but today (${fmt(serverToday)}) is outside this period, so the app stays locked.`
      });
      if (unlocked) {
        setTimeout(() => window.location.reload(), 1200);
      } else {
        await load();
      }
    } catch (e) {
      setMsg({ type: 'err', text: e.message });
    } finally {
      setSaving(false);
    }
  };

  return ReactDOM.createPortal(
    <div className="swm-overlay" onClick={onClose}>
      <div className="swm-box" style={{ maxWidth: 620 }} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="My subscription period">
        <div className="swm-header">
          <div>
            <h2>🗓️ My Subscription Period</h2>
            <p>Pick a start date. Access runs for exactly one month from that day.</p>
          </div>
          <button className="swm-close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        <div className="swm-detail">
          {!biz && !msg && <div className="swm-empty">Loading…</div>}

          {biz && (
            <>
              <div className="swm-current">
                <div className="swm-stat"><label>Business</label><strong>#{biz.businessId} {biz.businessName}</strong><div className="swm-sub">{biz.ownerEmail || ''}</div></div>
                <div className="swm-stat"><label>Current period</label>
                  <strong>{biz.window.end ? `${fmt(biz.window.start)} → ${fmt(biz.window.end)}` : 'Not set'}</strong>
                  {biz.window.updatedAt && <div className="swm-sub">set {fmtDateTime(biz.window.updatedAt)}</div>}
                </div>
                <div className="swm-stat"><label>Status</label>
                  <strong>{biz.isActive ? 'Active' : 'Locked'}</strong>
                  <div className="swm-sub">{biz.accessUntil ? `until ${fmtDateTime(biz.accessUntil)}` : ''}</div>
                </div>
              </div>

              {nextPeriodStart && (
                <div className="swm-actions" style={{ marginTop: 0 }}>
                  <button
                    className="swm-btn ghost"
                    onClick={() => { setStartDate(nextPeriodStart); setMsg(null); }}
                  >
                    ⏭ Next one-month period ({fmt(nextPeriodStart)} → {fmt(addOneMonth(nextPeriodStart))})
                  </button>
                </div>
              )}

              <div className="swm-section-title">New period — click the start date</div>
              <MonthCalendar start={startDate} end={endDate} onPickStart={(d) => { setStartDate(d); setMsg(null); }} minDate={minDate} maxDate={maxDate} />

              <div className="swm-preview">
                <div>
                  <div className="swm-sub">Start (selected)</div>
                  <div className="swm-date">{fmt(startDate)}</div>
                </div>
                <div className="swm-arrow">→</div>
                <div>
                  <div className="swm-sub">End <span className="swm-lock">🔒 start + 1 month</span></div>
                  <div className="swm-date">{fmt(endDate)}</div>
                </div>
              </div>

              <textarea
                className="swm-reason"
                placeholder="Note (optional)"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                maxLength={500}
              />

              {warnings.length > 0 && !unchanged && (
                <div className="swm-confirm">
                  {warnings.map((w, i) => <div key={i}>⚠️ {w}</div>)}
                </div>
              )}

              <div className="swm-actions">
                <button className="swm-btn primary" disabled={!startDate || unchanged || saving} onClick={save}>
                  {saving ? 'Saving…' : unchanged ? 'No change' : `Move subscription to ${fmt(startDate)} → ${fmt(endDate)}`}
                </button>
              </div>

              {msg && <div className={`swm-msg ${msg.type}`}>{msg.text}</div>}

              {detail.history?.length > 0 && (
                <div className="swm-history">
                  <div className="swm-section-title">History</div>
                  <table>
                    <thead><tr><th>When</th><th>From</th><th>To</th><th>By</th></tr></thead>
                    <tbody>
                      {detail.history.map((h) => (
                        <tr key={h.log_id}>
                          <td>{fmtDateTime(h.changed_at)}</td>
                          <td>{h.old_end ? `${fmt(h.old_start)} → ${fmt(h.old_end)}` : '—'}</td>
                          <td>{h.new_end ? `${fmt(h.new_start)} → ${fmt(h.new_end)}` : '—'}</td>
                          <td>{h.changed_by_email || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
          {!biz && msg && <div className={`swm-msg ${msg.type}`}>{msg.text}</div>}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default MySubscriptionModal;
